import { translate } from '../../i18n/index.js';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CalendarDays,
  Check,
  Clock3,
  Crown,
  Dumbbell,
  LockKeyhole,
  Sparkles,
  UserRound,
  X,
} from 'lucide-react';
import type { MembershipPlan, ProgramLeadInput } from '@atomfit/shared';
import { marketingApi } from '../../api/marketing.api.js';
import { membershipsApi } from '../../api/memberships.api.js';
import { Spinner } from '../design/AtomFitUI.js';
import { MembershipPlanCards } from '../membership/MembershipPlanCards.js';
import { useMembershipCheckoutStore } from '../../store/membership-checkout.store.js';

const PRIVACY_CONSENT = 'zpracováním osobních údajů';

type PricingTab = 'entry' | 'membership' | 'programs';

interface ProgramOffer {
  id: ProgramLeadInput['program'];
  name: string;
  ctaLabel: string;
  price: string;
  summary: string;
  details: string[];
  tone: 'teal' | 'gold';
}

const programModalStory = {
  eyebrow: translate('components.landing.pricing_section.program_modal_story.eyebrow'),
  title: translate('components.landing.pricing_section.program_modal_story.title'),
  accent: translate('components.landing.pricing_section.program_modal_story.accent'),
  description: translate('components.landing.pricing_section.program_modal_story.training'),
  steps: [
    [
      translate('components.landing.pricing_section.program_modal_story.steps'),
      translate('components.landing.pricing_section.program_modal_story.goals_expectations'),
    ],

    [
      translate('components.landing.pricing_section.program_modal_story.training_status'),
      translate('components.landing.pricing_section.program_modal_story.training_2'),
    ],

    [
      translate('components.landing.pricing_section.program_modal_story.steps_2'),
      translate('components.landing.pricing_section.program_modal_story.training_3'),
    ],
  ] as Array<[string, string]>,
  priceDetail: translate('components.landing.pricing_section.program_modal_story.price_detail'),
};

const tabs: Array<{ id: PricingTab; label: string; icon: typeof Dumbbell }> = [
  {
    id: 'entry',
    label: translate('components.landing.pricing_section.tabs.entry'),
    icon: Dumbbell,
  },
  {
    id: 'membership',
    label: translate('components.landing.pricing_section.tabs.membership'),
    icon: Crown,
  },
  {
    id: 'programs',
    label: translate('components.landing.pricing_section.tabs.program'),
    icon: CalendarDays,
  },
];

const programOffers: ProgramOffer[] = [
  {
    id: 'INTRO',
    name: translate('components.landing.pricing_section.program_offers.name'),
    ctaLabel: translate('components.landing.pricing_section.program_offers.cta_label'),
    price: translate('components.landing.pricing_section.program_offers.price'),
    summary: translate('components.landing.pricing_section.program_offers.summary'),
    details: [
      translate('components.landing.pricing_section.program_offers.details'),
      translate('components.landing.pricing_section.program_offers.details_2'),
      translate('components.landing.pricing_section.program_offers.details_3'),
      translate('components.landing.pricing_section.program_offers.details_4'),
      translate('components.landing.pricing_section.program_offers.details_5'),
    ],

    tone: 'teal',
  },
  {
    id: 'TRANSFORMATION_6',
    name: translate('components.landing.pricing_section.program_offers.name_2'),
    ctaLabel: translate('components.landing.pricing_section.program_offers.cta_label_2'),
    price: translate('components.landing.pricing_section.program_offers.price_2'),
    summary: translate('components.landing.pricing_section.program_offers.status'),
    details: [
      translate('components.landing.pricing_section.program_offers.details_6'),
      translate('components.landing.pricing_section.program_offers.details_7'),
      translate('components.landing.pricing_section.program_offers.details_8'),
      translate('components.landing.pricing_section.program_offers.details_9'),
      translate('components.landing.pricing_section.program_offers.details_10'),
    ],

    tone: 'gold',
  },
  {
    id: 'TRANSFORMATION_12',
    name: translate('components.landing.pricing_section.program_offers.name_3'),
    ctaLabel: translate('components.landing.pricing_section.program_offers.cta_label_3'),
    price: translate('components.landing.pricing_section.program_offers.price_3'),
    summary: translate('components.landing.pricing_section.program_offers.program_currency'),
    details: [
      translate('components.landing.pricing_section.program_offers.details_11'),
      translate('components.landing.pricing_section.program_offers.details_12'),
      translate('components.landing.pricing_section.program_offers.details_13'),
      translate('components.landing.pricing_section.program_offers.details_14'),
      translate('components.landing.pricing_section.program_offers.details_15'),
    ],

    tone: 'gold',
  },
];

export function PricingSection() {
  const navigate = useNavigate();
  const setPendingPlan = useMembershipCheckoutStore((state) => state.setPendingPlan);
  const [activeTab, setActiveTab] = useState<PricingTab>('entry');
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState(false);
  const [plansRequestKey, setPlansRequestKey] = useState(0);
  const [selectedProgram, setSelectedProgram] = useState<ProgramOffer | null>(null);

  useEffect(() => {
    if (activeTab !== 'membership' || plans.length > 0) return;

    let cancelled = false;
    setPlansLoading(true);
    setPlansError(false);
    membershipsApi
      .plans()
      .then((response) => {
        if (cancelled) return;
        setPlans(
          response.data.data.filter((plan) => plan.tier === 'PREMIUM' || plan.tier === 'ELITE'),
        );
      })
      .catch(() => {
        if (!cancelled) setPlansError(true);
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, plans.length, plansRequestKey]);

  function chooseMembership(plan: MembershipPlan) {
    setPendingPlan(plan);
    void navigate('/gyms?flow=membership');
  }

  return (
    <>
      <section
        className="af-shell af-landing-section af-pricing af-landing-surface--white af-scroll-reveal"
        id="pricing"
        aria-labelledby="af-pricing-title"
      >
        <div className="af-pricing-head">
          <p className="af-kicker">
            {translate('components.landing.pricing_section.pricing_section.p_kicker.pricing')}
          </p>
          <h2 id="af-pricing-title">
            {translate('components.landing.pricing_section.pricing_section.h2.membership')}
            <span className="af-landing-title-dot">{'.'}</span>
            <span className="af-pricing-head-accent">
              {translate(
                'components.landing.pricing_section.pricing_section.span_pricing_head_accent.program',
              )}
              <span className="af-landing-title-dot">{'.'}</span>
            </span>
          </h2>
          <p>
            {translate('components.landing.pricing_section.pricing_section.p.membership_select')}
          </p>
        </div>

        <div
          className="af-pricing-tabs"
          role="tablist"
          aria-label={translate(
            'components.landing.pricing_section.pricing_section.div_pricing_tabs.aria_label_pricing',
          )}
        >
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={`af-pricing-tab-${id}`}
              aria-controls={`af-pricing-panel-${id}`}
              aria-selected={activeTab === id}
              className={activeTab === id ? 'is-active' : undefined}
              onClick={() => setActiveTab(id)}
            >
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        <div
          className="af-pricing-panel"
          id={`af-pricing-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`af-pricing-tab-${activeTab}`}
          key={activeTab}
        >
          {activeTab === 'entry' && <EntryPricing />}
          {activeTab === 'membership' && (
            <MembershipPricing
              loading={plansLoading}
              error={plansError}
              plans={plans}
              onChoose={chooseMembership}
              onRetry={() => {
                setPlansError(false);
                setPlansRequestKey((current) => current + 1);
              }}
            />
          )}
          {activeTab === 'programs' && <ProgramPricing onChoose={setSelectedProgram} />}
        </div>

        <div
          className="af-pricing-assurances"
          aria-label={translate(
            'components.landing.pricing_section.pricing_section.div_pricing_assurances.aria_label_content',
          )}
        >
          <div>
            <Clock3 />
            <span>
              <strong>
                {translate('components.landing.pricing_section.pricing_section.strong.book')}
              </strong>
              {translate('components.landing.pricing_section.pricing_section.span.select')}
            </span>
          </div>
          <div>
            <LockKeyhole />
            <span>
              <strong>
                {translate('components.landing.pricing_section.pricing_section.strong.heading')}
              </strong>
              {translate('components.landing.pricing_section.pricing_section.span.no_waiting')}
            </span>
          </div>
          <div>
            <Sparkles />
            <span>
              <strong>
                {translate('components.landing.pricing_section.pricing_section.strong.heading_2')}
              </strong>
              {translate('components.landing.pricing_section.pricing_section.span.amenities')}
            </span>
          </div>
        </div>
        <p className="af-pricing-vat">
          {translate(
            'components.landing.pricing_section.pricing_section.p_pricing_vat.description',
          )}
        </p>
      </section>

      {selectedProgram ? (
        <ProgramLeadModal program={selectedProgram} onClose={() => setSelectedProgram(null)} />
      ) : null}
    </>
  );
}

function EntryPricing() {
  return (
    <div className="af-pricing-layout af-pricing-layout--entry">
      <div className="af-pricing-intro">
        <h3>
          {translate('components.landing.pricing_section.entry_pricing.h3.entry')}
          <span className="af-landing-title-dot">{'.'}</span>
        </h3>
        <p>{translate('components.landing.pricing_section.entry_pricing.p.book')}</p>
      </div>
      <article className="af-price-card af-price-card--entry">
        <span className="af-price-card-badge">
          {translate('components.landing.pricing_section.entry_pricing.span_price_card_badge.copy')}
        </span>
        <strong className="af-price-card-price">
          {translate(
            'components.landing.pricing_section.entry_pricing.strong_price_card_price.heading',
          )}
        </strong>
        <span className="af-price-card-period">
          {translate(
            'components.landing.pricing_section.entry_pricing.span_price_card_period.time',
          )}
        </span>
        <FeatureList
          items={[
            translate('components.landing.pricing_section.entry_pricing.features.private_fitness'),
            translate('components.landing.pricing_section.entry_pricing.features.access'),
            translate('components.landing.pricing_section.entry_pricing.features.amenities'),
          ]}
        />

        <Link className="af-price-card-button" to="/gyms">
          {translate(
            'components.landing.pricing_section.entry_pricing.link_price_card_button.book',
          )}
          <ArrowRight />
        </Link>
      </article>
    </div>
  );
}

function MembershipPricing({
  loading,
  error,
  plans,
  onChoose,
  onRetry,
}: {
  loading: boolean;
  error: boolean;
  plans: MembershipPlan[];
  onChoose: (plan: MembershipPlan) => void;
  onRetry: () => void;
}) {
  return (
    <div className="af-pricing-layout af-pricing-layout--membership">
      <div className="af-pricing-intro">
        <h3>
          {translate('components.landing.pricing_section.membership_pricing.h3.membership')}
          <span className="af-landing-title-dot">{'.'}</span>
        </h3>
        <p>
          {translate('components.landing.pricing_section.membership_pricing.p.membership_select')}
        </p>
      </div>
      {loading || error ? (
        <div className="af-membership-price-cards">
          {loading ? (
            <div className="af-pricing-loading">
              <Spinner />{' '}
              {translate(
                'components.landing.pricing_section.membership_pricing.div_pricing_loading.loading_membership',
              )}
            </div>
          ) : (
            <div className="af-pricing-loading">
              {translate(
                'components.landing.pricing_section.membership_pricing.div_pricing_loading.error_membership',
              )}

              <button type="button" className="af-outline-btn" onClick={onRetry}>
                {translate(
                  'components.landing.pricing_section.membership_pricing.button_outline_btn.retry',
                )}
              </button>
            </div>
          )}
        </div>
      ) : (
        <MembershipPlanCards plans={plans} onChoose={onChoose} />
      )}
    </div>
  );
}

function ProgramPricing({ onChoose }: { onChoose: (program: ProgramOffer) => void }) {
  return (
    <div className="af-pricing-layout af-pricing-layout--programs">
      <div className="af-pricing-intro">
        <h3>
          {translate('components.landing.pricing_section.program_pricing.h3.program')}
          <span className="af-landing-title-dot">{'.'}</span>
        </h3>
        <p>
          {translate('components.landing.pricing_section.program_pricing.p.training_nutrition')}
        </p>
      </div>
      <div className="af-program-price-cards">
        {programOffers.map((program) => (
          <article
            className={`af-price-card af-price-card--program af-price-card--${program.tone}`}
            key={program.id}
          >
            <div className="af-program-card-icon" aria-hidden="true">
              {program.id === 'INTRO' ? <UserRound /> : <CalendarDays />}
            </div>
            <h4>
              {program.id === 'INTRO' ? (
                <>
                  {translate('components.landing.pricing_section.program_pricing.h4.heading')}
                  <br />
                  {translate('components.landing.pricing_section.program_pricing.h4.training')}
                </>
              ) : (
                program.name
              )}
            </h4>
            <strong className="af-price-card-price">{program.price}</strong>
            <p>{program.summary}</p>
            <FeatureList items={program.details} />
            <button
              className="af-price-card-button"
              type="button"
              onClick={() => onChoose(program)}
            >
              {program.ctaLabel} <ArrowRight />
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}

function FeatureList({ items }: { items: string[] }) {
  return (
    <ul className="af-price-features">
      {items.map((item) => (
        <li key={item}>
          <Check aria-hidden="true" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function ProgramLeadModal({ program, onClose }: { program: ProgramOffer; onClose: () => void }) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    if (data.get('consent') !== 'on') return;
    setStatus('submitting');

    try {
      await marketingApi.submitProgramLead({
        firstName: String(data.get('firstName') ?? ''),
        lastName: String(data.get('lastName') ?? ''),
        email: String(data.get('email') ?? ''),
        phone: String(data.get('phone') ?? ''),
        city: String(data.get('city') ?? ''),
        preferredCallTime: String(
          data.get('preferredCallTime') ?? 'ANYTIME',
        ) as ProgramLeadInput['preferredCallTime'],
        program: program.id,
        consent: true,
        website: String(data.get('website') ?? ''),
      });
      form.reset();
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  return createPortal(
    <div
      className="af-program-modal-shell"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="af-program-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="af-program-modal-title"
      >
        <button
          ref={closeButtonRef}
          className="af-program-modal-close"
          type="button"
          aria-label={translate(
            'components.landing.pricing_section.program_lead_modal.button_program_modal_close.aria_label_close',
          )}
          onClick={onClose}
        >
          <X />
        </button>

        <aside className="af-program-modal-story">
          <span>{programModalStory.eyebrow}</span>
          <h2 id="af-program-modal-title">
            {programModalStory.title}
            <strong>
              {programModalStory.accent}
              <i>{'.'}</i>
            </strong>
          </h2>
          <p>{programModalStory.description}</p>
          <ol>
            {programModalStory.steps.map(([title, description], index) => (
              <li key={title}>
                <span>{index + 1}</span>
                <div>
                  <strong>{title}</strong>
                  <p>{description}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="af-program-modal-price">
            <Clock3 />
            <span>
              <strong>
                {translate('components.landing.pricing_section.program_lead_modal.strong.price')}{' '}
                {program.price}
              </strong>
              {programModalStory.priceDetail}
            </span>
          </div>
        </aside>

        <div className="af-program-modal-form-wrap">
          {status === 'success' ? (
            <div className="af-program-form-success" role="status">
              <span>
                <Check />
              </span>
              <h2>
                {translate('components.landing.pricing_section.program_lead_modal.h2.heading')}
              </h2>
              <p>{translate('components.landing.pricing_section.program_lead_modal.p.next')}</p>
              <button className="af-price-card-button" type="button" onClick={onClose}>
                {translate(
                  'components.landing.pricing_section.program_lead_modal.button_price_card_button.close',
                )}
              </button>
            </div>
          ) : (
            <>
              <h2>
                {translate('components.landing.pricing_section.program_lead_modal.h2.heading_2')}
                <strong>
                  {translate(
                    'components.landing.pricing_section.program_lead_modal.strong.heading',
                  )}
                  <span>{'.'}</span>
                </strong>
              </h2>
              <p>{translate('components.landing.pricing_section.program_lead_modal.p.training')}</p>
              <form className="af-program-form" onSubmit={(event) => void handleSubmit(event)}>
                <div className="af-program-form-row">
                  <label>
                    {translate('components.landing.pricing_section.program_lead_modal.label.name')}
                    <input
                      name="firstName"
                      autoComplete="given-name"
                      placeholder={translate(
                        'components.landing.pricing_section.program_lead_modal.input.placeholder_name',
                      )}
                      required
                      maxLength={100}
                    />
                  </label>
                  <label>
                    {translate(
                      'components.landing.pricing_section.program_lead_modal.label.last_name',
                    )}
                    <input
                      name="lastName"
                      autoComplete="family-name"
                      placeholder={translate(
                        'components.landing.pricing_section.program_lead_modal.input.placeholder_last_name',
                      )}
                      required
                      maxLength={100}
                    />
                  </label>
                </div>
                <label>
                  {translate('components.landing.pricing_section.program_lead_modal.label.email')}
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder={translate(
                      'components.landing.pricing_section.program_lead_modal.input.placeholder_email',
                    )}
                    required
                    maxLength={254}
                  />
                </label>
                <label>
                  {translate('components.landing.pricing_section.program_lead_modal.label.phone')}
                  <input
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder={translate(
                      'components.landing.pricing_section.program_lead_modal.input.placeholder_phone',
                    )}
                    required
                    minLength={6}
                    maxLength={40}
                  />
                </label>
                <label>
                  {translate('components.landing.pricing_section.program_lead_modal.label.city')}
                  <input
                    name="city"
                    autoComplete="address-level2"
                    placeholder={translate(
                      'components.landing.pricing_section.program_lead_modal.input.placeholder_content',
                    )}
                    required
                    maxLength={100}
                  />
                </label>
                <label>
                  {translate('components.landing.pricing_section.program_lead_modal.label.copy')}
                  <select name="preferredCallTime" defaultValue="" required>
                    <option value="" disabled>
                      {translate(
                        'components.landing.pricing_section.program_lead_modal.option.select',
                      )}
                    </option>
                    <option value="MORNING">
                      {translate(
                        'components.landing.pricing_section.program_lead_modal.option.label',
                      )}
                    </option>
                    <option value="AFTERNOON">
                      {translate(
                        'components.landing.pricing_section.program_lead_modal.option.label_2',
                      )}
                    </option>
                    <option value="EVENING">
                      {translate(
                        'components.landing.pricing_section.program_lead_modal.option.label_3',
                      )}
                    </option>
                    <option value="ANYTIME">
                      {translate(
                        'components.landing.pricing_section.program_lead_modal.option.any_time',
                      )}
                    </option>
                  </select>
                </label>
                <label className="af-program-form-honeypot" aria-hidden="true">
                  {translate(
                    'components.landing.pricing_section.program_lead_modal.label_program_form_honeypot.website',
                  )}
                  <input name="website" tabIndex={-1} autoComplete="off" />
                </label>
                <label className="af-program-form-consent">
                  <input name="consent" type="checkbox" required />
                  <span>
                    {translate(
                      'components.landing.pricing_section.program_lead_modal.span.consent',
                    )}{' '}
                    <Link to="/privacy" target="_blank">
                      {PRIVACY_CONSENT}
                    </Link>
                    {'.'}
                  </span>
                </label>
                {status === 'error' ? (
                  <div className="af-program-form-error" role="alert">
                    {translate(
                      'components.landing.pricing_section.program_lead_modal.div_program_form_error.error_sent',
                    )}
                  </div>
                ) : null}
                <button
                  className="af-program-form-submit"
                  type="submit"
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting'
                    ? translate(
                        'components.landing.pricing_section.program_lead_modal.button_program_form_submit.label',
                      )
                    : program.ctaLabel}{' '}
                  <ArrowRight />
                </button>
                <small>
                  <LockKeyhole />{' '}
                  {translate(
                    'components.landing.pricing_section.program_lead_modal.small.description',
                  )}
                </small>
              </form>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
